import { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ToggleStyleSwitch from '@CommonRepresentationalDisplay/Switch/ToggleStyleSwitch';
import ColorPicker from '@CommonFunctionalDisplay/ColorPicker';
import EmailDesigner, { TEMPLATES as EMAIL_DESIGNER_TEMPLATES, BLOCK_TYPES as EMAIL_BLOCK_TYPES } from '../NotificationSettings/EmailDesigner';
import FieldService from '@Helpers/URLHelpers/FieldService';
import ErrorHandler from '@Root/ErrorHandler';
import { BsChevronUp as ShiftUp, BsChevronDown as ShiftDown } from 'react-icons/bs';
import { BsXSquare as BsClose } from 'react-icons/bs';
import { IoLogoWhatsapp as WhatsappIcon } from 'react-icons/io5';
import { MdEdit as Edit, MdDeleteForever as Delete } from 'react-icons/md';
import { MdSearch as SearchIcon, MdClose as XIcon, MdMessage as MessageIcon, MdMailOutline as MailOutline } from 'react-icons/md';
import { MdInfo as Info } from 'react-icons/md';
import { STATUS_TEMPLATES, SYSTEM_STATUS_VALUES } from './statusTemplates';

const cn = 'issueConsole__settingsPage';

const STATUS_PRESET_REGISTRY = [
    // value: reported
    { presetId: 101, title: "Ticket Received",          value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 101 },
    { presetId: 102, title: "Request Submitted",         value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 102 },
    { presetId: 103, title: "Issue Logged",              value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 103 },
    { presetId: 104, title: "Query Registered",          value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 104 },
    { presetId: 105, title: "Support Request Received",  value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 105 },
    { presetId: 106, title: "Complaint Registered",      value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 106 },
    { presetId: 107, title: "Feedback Recorded",         value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 107 },
    { presetId: 108, title: "Application Received",      value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 108 },
    { presetId: 109, title: "Booking Request Submitted", value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 109 },
    { presetId: 110, title: "Order Raised",              value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 110 },
    { presetId: 111, title: "Case Opened",               value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 111 },
    { presetId: 112, title: "Incident Reported",         value: "reported",  defaultColor: "#7F78FF", whatsappVariantId: 112 },
    // value: open
    { presetId: 201, title: "In Progress",               value: "open", defaultColor: "#40c47d", whatsappVariantId: 201 },
    { presetId: 202, title: "Under Review",              value: "open", defaultColor: "#40c47d", whatsappVariantId: 202 },
    { presetId: 203, title: "Being Investigated",        value: "open", defaultColor: "#40c47d", whatsappVariantId: 203 },
    { presetId: 204, title: "Assigned",                  value: "open", defaultColor: "#40c47d", whatsappVariantId: 204 },
    { presetId: 205, title: "Pending Approval",          value: "open", defaultColor: "#FDB16C", whatsappVariantId: 205 },
    { presetId: 206, title: "In Queue",                  value: "open", defaultColor: "#40c47d", whatsappVariantId: 206 },
    { presetId: 207, title: "On Hold",                   value: "open", defaultColor: "#FDB16C", whatsappVariantId: 207 },
    { presetId: 208, title: "Scheduled",                 value: "open", defaultColor: "#40c47d", whatsappVariantId: 208 },
    { presetId: 209, title: "Awaiting Your Response",    value: "open", defaultColor: "#FDB16C", whatsappVariantId: 209 },
    { presetId: 210, title: "Being Processed",           value: "open", defaultColor: "#40c47d", whatsappVariantId: 210 },
    { presetId: 211, title: "Second Level Review",       value: "open", defaultColor: "#FDB16C", whatsappVariantId: 211 },
    { presetId: 212, title: "Awaiting Resources",        value: "open", defaultColor: "#FDB16C", whatsappVariantId: 212 },
    { presetId: 213, title: "In Verification",           value: "open", defaultColor: "#40c47d", whatsappVariantId: 213 },
    { presetId: 214, title: "With Third Party",          value: "open", defaultColor: "#FDB16C", whatsappVariantId: 214 },
    // value: resolved
    { presetId: 301, title: "Resolved",                  value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 301 },
    { presetId: 302, title: "Closed",                    value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 302 },
    { presetId: 303, title: "Closed by Reporter",        value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 303 },
    { presetId: 304, title: "Closed without Resolution", value: "resolved", defaultColor: "#64748b", whatsappVariantId: 304 },
    { presetId: 305, title: "Lost",                      value: "resolved", defaultColor: "#64748b", whatsappVariantId: 305 },
    { presetId: 306, title: "Duplicate",                 value: "resolved", defaultColor: "#64748b", whatsappVariantId: 306 },
    { presetId: 307, title: "No Action Required",        value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 307 },
    { presetId: 308, title: "Completed",                 value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 308 },
    { presetId: 309, title: "Fulfilled",                 value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 309 },
    { presetId: 310, title: "Not Reproducible",          value: "resolved", defaultColor: "#64748b", whatsappVariantId: 310 },
    { presetId: 311, title: "Withdrawn",                 value: "resolved", defaultColor: "#64748b", whatsappVariantId: 311 },
    { presetId: 312, title: "Expired",                   value: "resolved", defaultColor: "#64748b", whatsappVariantId: 312 },
    { presetId: 313, title: "Cancelled",                 value: "resolved", defaultColor: "#64748b", whatsappVariantId: 313 },
    { presetId: 314, title: "Declined",                  value: "resolved", defaultColor: "#64748b", whatsappVariantId: 314 },
    { presetId: 315, title: "Referred Externally",       value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 315 },
    { presetId: 316, title: "Under Warranty",            value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 316 },
    { presetId: 317, title: "Service Delivered",         value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 317 },
    { presetId: 318, title: "Problem Resolved",          value: "resolved", defaultColor: "#2784B9", whatsappVariantId: 318 },
    // value: reopened
    { presetId: 401, title: "Reopened",                  value: "reopened", defaultColor: "#FF8628", whatsappVariantId: 401 },
    { presetId: 402, title: "Re-activated",              value: "reopened", defaultColor: "#FF8628", whatsappVariantId: 402 },
    { presetId: 403, title: "Follow-up Required",        value: "reopened", defaultColor: "#FF8628", whatsappVariantId: 403 },
    { presetId: 404, title: "Issue Persisting",          value: "reopened", defaultColor: "#FF8628", whatsappVariantId: 404 },
    { presetId: 405, title: "Pending Verification",      value: "reopened", defaultColor: "#FDB16C", whatsappVariantId: 405 },
    { presetId: 406, title: "Under Re-investigation",    value: "reopened", defaultColor: "#FF8628", whatsappVariantId: 406 },
    { presetId: 407, title: "Customer Dissatisfied",     value: "reopened", defaultColor: "#FB392D", whatsappVariantId: 407 },
    { presetId: 408, title: "Additional Work Required",  value: "reopened", defaultColor: "#FF8628", whatsappVariantId: 408 },
    { presetId: 409, title: "Quality Check Failed",      value: "reopened", defaultColor: "#FB392D", whatsappVariantId: 409 },
    { presetId: 410, title: "Resolution Disputed",       value: "reopened", defaultColor: "#FB392D", whatsappVariantId: 410 },
    { presetId: 411, title: "Escalated after Closure",   value: "reopened", defaultColor: "#FB392D", whatsappVariantId: 411 },
    { presetId: 412, title: "Regression Detected",       value: "reopened", defaultColor: "#FB392D", whatsappVariantId: 412 },
    // value: escalated
    { presetId: 501, title: "Escalated",                       value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 501 },
    { presetId: 502, title: "Priority Raised",                 value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 502 },
    { presetId: 503, title: "Urgent",                          value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 503 },
    { presetId: 504, title: "Flagged for Management Review",   value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 504 },
    { presetId: 505, title: "Critical",                        value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 505 },
    { presetId: 506, title: "Referred to Specialist",          value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 506 },
    { presetId: 507, title: "SLA Breach",                      value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 507 },
    { presetId: 508, title: "Complaint Escalated",             value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 508 },
    { presetId: 509, title: "High Priority Client",            value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 509 },
    { presetId: 510, title: "Under Legal Review",              value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 510 },
    { presetId: 511, title: "Regulatory Matter Flagged",       value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 511 },
    { presetId: 512, title: "Breach of Policy Identified",     value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 512 },
    { presetId: 513, title: "Awaiting Senior Approval",        value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 513 },
    { presetId: 514, title: "Cross-team Escalation",           value: "escalated", defaultColor: "#FB392D", whatsappVariantId: 514 },
];

const WHATSAPP_VARIANT_MESSAGES = {
    101: "Your ticket *#{ticketId}* has been received by *{orgName}*. We will review it shortly and get back to you.",
    102: "Your request *#{ticketId}* has been successfully submitted to *{formTitle}*. Our team will be in touch soon.",
    103: "Your issue has been logged as *#{ticketId}*. A support agent will be assigned shortly.",
    104: "We have registered your query *#{ticketId}* and it is now under review. You will receive an update from our team.",
    105: "Thank you for reaching out. Your support request *#{ticketId}* has been received and is being reviewed.",
    106: "Your complaint *#{ticketId}* has been formally registered with *{orgName}*. We take this seriously and will respond within the promised timeframe.",
    107: "Your feedback *#{ticketId}* has been recorded with *{formTitle}*. Thank you for helping us improve.",
    108: "Your application *#{ticketId}* has been received and is now pending review. We will notify you of next steps.",
    109: "Your booking request *#{ticketId}* has been submitted to *{orgName}*. Our team will confirm the details with you shortly.",
    110: "Your order *#{ticketId}* has been raised successfully. Our team will process it and keep you updated.",
    111: "A new case *#{ticketId}* has been opened on your behalf with *{orgName}*. You will be notified as it progresses.",
    112: "Your incident *#{ticketId}* has been reported. Our team will assess the situation and respond promptly.",
    201: "Your ticket *#{ticketId}* is now being actively worked on. We will update you as we make progress.",
    202: "Your request *#{ticketId}* is currently under review by our team. We will share our findings shortly.",
    203: "Our team is currently investigating the issue you reported in *#{ticketId}*. We appreciate your patience.",
    204: "Your ticket *#{ticketId}* has been assigned to a team member. They will be in touch with you directly.",
    205: "Your request *#{ticketId}* is pending approval. You will be notified once a decision has been made.",
    206: "Your ticket *#{ticketId}* is in our queue and will be attended to in the order it was received.",
    207: "Your ticket *#{ticketId}* has been put on hold temporarily. We will resume work and update you as soon as possible.",
    208: "Work on your ticket *#{ticketId}* has been scheduled. You will receive details on the timeline shortly.",
    209: "We need some additional information to proceed with *#{ticketId}*. Please reply to this message at your earliest convenience.",
    210: "Your request *#{ticketId}* is currently being processed. This may take a little time and we will keep you posted.",
    211: "Your ticket *#{ticketId}* has been moved to a second level review. A specialist will be looking into this for you.",
    212: "We are waiting on the necessary resources to resolve *#{ticketId}*. We will update you once they are available.",
    213: "We are currently verifying the details of your request *#{ticketId}* before proceeding.",
    214: "Your ticket *#{ticketId}* has been escalated to a third-party vendor. We are awaiting their response and will keep you updated.",
    301: "Your ticket *#{ticketId}* has been resolved. If the issue persists, you can reopen it at any time.",
    302: "Your ticket *#{ticketId}* has been closed. Thank you for reaching out to *{orgName}*.",
    303: "We have received your request to close ticket *#{ticketId}*. It has now been closed as per your request.",
    304: "Unfortunately, we were unable to resolve ticket *#{ticketId}* at this time. It has been closed. Please feel free to raise a new ticket if the issue continues.",
    305: "Ticket *#{ticketId}* has been marked as closed. We are sorry we could not assist further on this occasion.",
    306: "Ticket *#{ticketId}* has been identified as a duplicate of an existing request and has been closed. Your original ticket remains active.",
    307: "After reviewing your request *#{ticketId}*, no further action is required. This ticket has been closed.",
    308: "The work on your request *#{ticketId}* has been completed. This ticket is now closed.",
    309: "Your request *#{ticketId}* has been fulfilled by *{orgName}*. This ticket is now closed.",
    310: "We were unable to reproduce the issue reported in *#{ticketId}*. The ticket has been closed. Please raise a new one if the issue recurs.",
    311: "Ticket *#{ticketId}* has been withdrawn. If you need to raise a new request in the future, we are always here to help.",
    312: "Ticket *#{ticketId}* has been closed as it has been inactive for an extended period. Feel free to raise a new ticket if you still need assistance.",
    313: "Ticket *#{ticketId}* has been cancelled as per your request. We hope to assist you again in the future.",
    314: "After careful review, your request *#{ticketId}* has been declined. If you have questions about this decision, please contact us directly.",
    315: "Your request *#{ticketId}* has been referred to the appropriate external team. This ticket is now closed on our end.",
    316: "Your issue in ticket *#{ticketId}* has been resolved under warranty. The ticket is now closed.",
    317: "The service you requested in *#{ticketId}* has been delivered. Thank you for choosing *{orgName}*.",
    318: "The problem reported in *#{ticketId}* has been resolved by our team. This ticket is now closed.",
    401: "Your ticket *#{ticketId}* has been reopened. Our team will resume work and update you shortly.",
    402: "Ticket *#{ticketId}* has been re-activated. A team member will pick it up and be in touch soon.",
    403: "A follow-up is required on ticket *#{ticketId}*. Our team will be in touch to address the outstanding concern.",
    404: "We have noted that the issue in *#{ticketId}* is persisting. The ticket has been reopened and our team is looking into it.",
    405: "The resolution for ticket *#{ticketId}* is pending verification. We will confirm once everything checks out.",
    406: "Ticket *#{ticketId}* has been reopened for re-investigation. Our team will keep you updated on the findings.",
    407: "We have noted that you were not satisfied with the resolution of *#{ticketId}*. The ticket has been reopened and we are committed to getting this right for you.",
    408: "We have identified that additional work is required for *#{ticketId}*. The ticket has been reopened accordingly.",
    409: "Ticket *#{ticketId}* has been reopened as the resolution did not meet our quality standards. We will ensure it is fully resolved before closing again.",
    410: "The proposed resolution for *#{ticketId}* has been disputed. Our team will review and respond with an updated resolution.",
    411: "Ticket *#{ticketId}* has been escalated for further review following its closure. A senior team member will be in touch.",
    412: "A regression related to ticket *#{ticketId}* has been detected. The ticket has been reopened for immediate attention.",
    501: "Your ticket *#{ticketId}* has been escalated to our senior team. You will receive priority attention from here.",
    502: "The priority of your ticket *#{ticketId}* has been raised. Our team will address it with urgency.",
    503: "Your ticket *#{ticketId}* has been marked as urgent and is receiving immediate attention from *{orgName}*.",
    504: "Your ticket *#{ticketId}* has been flagged for review by our management team. We take your concern very seriously.",
    505: "Your ticket *#{ticketId}* has been marked as critical. Our team is prioritising this and will update you shortly.",
    506: "Your ticket *#{ticketId}* has been referred to a specialist for further review. You can expect a detailed response from them.",
    507: "We acknowledge that ticket *#{ticketId}* has exceeded its expected resolution time. It has been escalated to ensure it is addressed immediately.",
    508: "Your formal complaint *#{ticketId}* has been escalated to our senior leadership. We will respond within the committed timeframe.",
    509: "Your ticket *#{ticketId}* has been escalated to ensure you receive our highest level of service. A dedicated team member has been assigned.",
    510: "Ticket *#{ticketId}* has been referred for legal review. Our compliance team will be in touch with you directly.",
    511: "Ticket *#{ticketId}* has been flagged as a potential regulatory matter and is being reviewed by the appropriate team.",
    512: "A breach of policy has been identified in relation to ticket *#{ticketId}*. The matter has been escalated for formal review.",
    513: "Your ticket *#{ticketId}* requires senior-level approval to proceed. Our team has been notified and will update you shortly.",
    514: "Ticket *#{ticketId}* has been shared across multiple teams for a coordinated response. We will keep you updated on progress.",
};

const EMAIL_VARIANT_DEFAULTS = {
    101: { theme: 'blue',   subject: 'Your ticket #{ticketId} has been received', body: 'Hi {reporterName},\n\nThank you for reaching out. Your ticket #{ticketId} has been received by {orgName} and is currently under review.\n\nWe will keep you updated as things progress.\n\n— The {orgName} Team' },
    102: { theme: 'blue',   subject: 'Submission confirmed — #{ticketId}', body: 'Hi {reporterName},\n\nYour request #{ticketId} has been successfully submitted to {formTitle}. Our team will be in touch with you soon.\n\n— The {orgName} Team' },
    103: { theme: 'blue',   subject: 'Support request #{ticketId} logged', body: 'Hi {reporterName},\n\nYour issue has been logged as #{ticketId}. A support agent will be assigned to your case shortly.\n\n— The {orgName} Team' },
    104: { theme: 'blue',   subject: 'Your query #{ticketId} is under review', body: 'Hi {reporterName},\n\nWe have registered your query #{ticketId} and it is now under review. You will receive an update from our team shortly.\n\n— The {orgName} Team' },
    105: { theme: 'blue',   subject: 'Support request #{ticketId} received', body: 'Hi {reporterName},\n\nThank you for reaching out. Your support request #{ticketId} has been received and is currently being reviewed by our team.\n\n— The {orgName} Team' },
    106: { theme: 'blue',   subject: 'Complaint #{ticketId} registered', body: 'Hi {reporterName},\n\nYour complaint #{ticketId} has been formally registered with {orgName}. We take this matter seriously and will respond within the promised timeframe.\n\n— The {orgName} Team' },
    107: { theme: 'blue',   subject: 'Feedback #{ticketId} recorded — thank you', body: 'Hi {reporterName},\n\nYour feedback #{ticketId} has been recorded with {formTitle}. Thank you for helping us improve — we genuinely appreciate it.\n\n— The {orgName} Team' },
    108: { theme: 'blue',   subject: 'Application #{ticketId} is pending review', body: 'Hi {reporterName},\n\nYour application #{ticketId} has been received and is now pending review. We will notify you of the next steps in due course.\n\n— The {orgName} Team' },
    109: { theme: 'blue',   subject: 'Booking request #{ticketId} submitted', body: 'Hi {reporterName},\n\nYour booking request #{ticketId} has been submitted to {orgName}. Our team will confirm the details with you shortly.\n\n— The {orgName} Team' },
    110: { theme: 'blue',   subject: 'Order #{ticketId} raised successfully', body: 'Hi {reporterName},\n\nYour order #{ticketId} has been raised successfully. Our team will process it and keep you updated along the way.\n\n— The {orgName} Team' },
    111: { theme: 'blue',   subject: 'Case #{ticketId} opened', body: 'Hi {reporterName},\n\nA new case #{ticketId} has been opened on your behalf with {orgName}. You will be notified as it progresses.\n\n— The {orgName} Team' },
    112: { theme: 'blue',   subject: 'Incident #{ticketId} reported', body: 'Hi {reporterName},\n\nYour incident #{ticketId} has been reported to our team. We will assess the situation and respond as promptly as possible.\n\n— The {orgName} Team' },
    201: { theme: 'blue',   subject: 'Ticket #{ticketId} — work in progress', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} is now being actively worked on by our team. We will update you as we make progress.\n\n— The {orgName} Team' },
    202: { theme: 'blue',   subject: 'Ticket #{ticketId} is under review', body: 'Hi {reporterName},\n\nYour request #{ticketId} is currently under review by our team. We will share our findings with you shortly.\n\n— The {orgName} Team' },
    203: { theme: 'blue',   subject: 'Investigating ticket #{ticketId}', body: 'Hi {reporterName},\n\nOur team is currently investigating the issue you reported in ticket #{ticketId}. We appreciate your patience while we look into this.\n\n— The {orgName} Team' },
    204: { theme: 'blue',   subject: 'Ticket #{ticketId} has been assigned', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been assigned to a team member who will be in touch with you directly.\n\n— The {orgName} Team' },
    205: { theme: 'blue',   subject: 'Ticket #{ticketId} — pending approval', body: 'Hi {reporterName},\n\nYour request #{ticketId} is pending approval. You will be notified as soon as a decision has been made.\n\n— The {orgName} Team' },
    206: { theme: 'blue',   subject: 'Ticket #{ticketId} is in our queue', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} is in our queue and will be attended to in the order it was received. Thank you for your patience.\n\n— The {orgName} Team' },
    207: { theme: 'blue',   subject: 'Ticket #{ticketId} temporarily on hold', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been put on hold temporarily. We will resume work and update you as soon as possible.\n\n— The {orgName} Team' },
    208: { theme: 'blue',   subject: 'Work on ticket #{ticketId} has been scheduled', body: 'Hi {reporterName},\n\nWork on your ticket #{ticketId} has been scheduled. You will receive details on the timeline shortly.\n\n— The {orgName} Team' },
    209: { theme: 'blue',   subject: 'Action required — ticket #{ticketId}', body: 'Hi {reporterName},\n\nWe need some additional information to proceed with ticket #{ticketId}. Please reply to this email at your earliest convenience so we can move forward.\n\n— The {orgName} Team' },
    210: { theme: 'blue',   subject: 'Ticket #{ticketId} is being processed', body: 'Hi {reporterName},\n\nYour request #{ticketId} is currently being processed. This may take a little time, and we will keep you posted on progress.\n\n— The {orgName} Team' },
    211: { theme: 'blue',   subject: 'Ticket #{ticketId} — second-level review', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been moved to a second-level review. A specialist will be looking into this for you.\n\n— The {orgName} Team' },
    212: { theme: 'blue',   subject: 'Ticket #{ticketId} — awaiting resources', body: 'Hi {reporterName},\n\nWe are waiting on the necessary resources to resolve ticket #{ticketId}. We will update you as soon as they are available.\n\n— The {orgName} Team' },
    213: { theme: 'blue',   subject: 'Verifying details for ticket #{ticketId}', body: 'Hi {reporterName},\n\nWe are currently verifying the details of your request #{ticketId} before proceeding. We will be in touch shortly.\n\n— The {orgName} Team' },
    214: { theme: 'blue',   subject: 'Ticket #{ticketId} referred to third-party vendor', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been escalated to a third-party vendor. We are awaiting their response and will keep you updated.\n\n— The {orgName} Team' },
    301: { theme: 'green',  subject: 'Ticket #{ticketId} has been resolved', body: 'Hi {reporterName},\n\nGreat news — your ticket #{ticketId} has been resolved. If the issue persists, you can reopen it at any time.\n\nThank you for reaching out to {orgName}.\n\n— The {orgName} Team' },
    302: { theme: 'green',  subject: 'Ticket #{ticketId} has been closed', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been closed. Thank you for reaching out to {orgName} — it was our pleasure to assist you.\n\n— The {orgName} Team' },
    303: { theme: 'green',  subject: 'Ticket #{ticketId} closed as requested', body: 'Hi {reporterName},\n\nWe have received your request to close ticket #{ticketId}. It has now been closed as per your instruction.\n\nIf you need further assistance, please do not hesitate to get in touch.\n\n— The {orgName} Team' },
    304: { theme: 'red',    subject: 'Unable to resolve ticket #{ticketId}', body: 'Hi {reporterName},\n\nUnfortunately, we were unable to resolve ticket #{ticketId} at this time. The ticket has been closed.\n\nIf the issue continues, please feel free to raise a new ticket and we will do our best to assist you.\n\n— The {orgName} Team' },
    305: { theme: 'red',    subject: 'Ticket #{ticketId} closed — apologies for any inconvenience', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been closed. We are sorry we could not assist further on this occasion.\n\nPlease do not hesitate to reach out if you need anything else.\n\n— The {orgName} Team' },
    306: { theme: 'orange', subject: 'Ticket #{ticketId} — duplicate request', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been identified as a duplicate of an existing request and has been closed. Your original ticket remains active and is being handled by our team.\n\n— The {orgName} Team' },
    307: { theme: 'green',  subject: 'No further action required — ticket #{ticketId}', body: 'Hi {reporterName},\n\nAfter reviewing your request #{ticketId}, no further action is required. This ticket has been closed.\n\nIf anything changes, please feel free to open a new request.\n\n— The {orgName} Team' },
    308: { theme: 'green',  subject: 'Work completed — ticket #{ticketId} closed', body: 'Hi {reporterName},\n\nThe work on your request #{ticketId} has been completed. This ticket is now closed.\n\nThank you for working with us — we hope we met your expectations.\n\n— The {orgName} Team' },
    309: { theme: 'green',  subject: 'Request #{ticketId} fulfilled', body: 'Hi {reporterName},\n\nYour request #{ticketId} has been fulfilled by {orgName}. This ticket is now closed.\n\nThank you for choosing {orgName}.\n\n— The {orgName} Team' },
    310: { theme: 'orange', subject: 'Unable to reproduce issue — ticket #{ticketId} closed', body: 'Hi {reporterName},\n\nWe were unable to reproduce the issue reported in ticket #{ticketId}. The ticket has been closed.\n\nIf the issue recurs, please raise a new ticket and we will investigate further.\n\n— The {orgName} Team' },
    311: { theme: 'orange', subject: 'Ticket #{ticketId} withdrawn', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been withdrawn. If you need to raise a new request in the future, we are always here to help.\n\n— The {orgName} Team' },
    312: { theme: 'orange', subject: 'Ticket #{ticketId} closed — inactivity', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been closed as it has been inactive for an extended period. If you still need assistance, please feel free to raise a new ticket.\n\n— The {orgName} Team' },
    313: { theme: 'green',  subject: 'Ticket #{ticketId} cancelled as requested', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been cancelled as per your request. We hope to assist you again in the future.\n\n— The {orgName} Team' },
    314: { theme: 'red',    subject: 'Request #{ticketId} declined', body: 'Hi {reporterName},\n\nAfter careful review, your request #{ticketId} has been declined. If you have any questions about this decision, please do not hesitate to contact us directly.\n\n— The {orgName} Team' },
    315: { theme: 'blue',   subject: 'Ticket #{ticketId} referred to external team', body: 'Hi {reporterName},\n\nYour request #{ticketId} has been referred to the appropriate external team. This ticket is now closed on our end.\n\nThe external team will be in touch with you regarding next steps.\n\n— The {orgName} Team' },
    316: { theme: 'green',  subject: 'Warranty resolution — ticket #{ticketId} closed', body: 'Hi {reporterName},\n\nThe issue in ticket #{ticketId} has been resolved under warranty. The ticket is now closed.\n\nThank you for reaching out to us.\n\n— The {orgName} Team' },
    317: { theme: 'green',  subject: 'Service delivered — ticket #{ticketId} closed', body: 'Hi {reporterName},\n\nThe service you requested in ticket #{ticketId} has been delivered. This ticket is now closed.\n\nThank you for choosing {orgName}.\n\n— The {orgName} Team' },
    318: { theme: 'green',  subject: 'Problem resolved — ticket #{ticketId} closed', body: 'Hi {reporterName},\n\nThe problem reported in ticket #{ticketId} has been resolved by our team. This ticket is now closed.\n\nWe appreciate your patience throughout the process.\n\n— The {orgName} Team' },
    401: { theme: 'orange', subject: 'Ticket #{ticketId} has been reopened', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been reopened. Our team will resume work and update you shortly.\n\n— The {orgName} Team' },
    402: { theme: 'orange', subject: 'Ticket #{ticketId} re-activated', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been re-activated. A team member will pick it up and be in touch with you soon.\n\n— The {orgName} Team' },
    403: { theme: 'orange', subject: 'Follow-up required — ticket #{ticketId}', body: 'Hi {reporterName},\n\nA follow-up is required on ticket #{ticketId}. Our team will be in touch to address the outstanding concern.\n\n— The {orgName} Team' },
    404: { theme: 'orange', subject: 'Ticket #{ticketId} reopened — issue persisting', body: 'Hi {reporterName},\n\nWe have noted that the issue in ticket #{ticketId} is persisting. The ticket has been reopened and our team is looking into it immediately.\n\n— The {orgName} Team' },
    405: { theme: 'orange', subject: 'Resolution pending verification — ticket #{ticketId}', body: 'Hi {reporterName},\n\nThe resolution for ticket #{ticketId} is pending verification. We will confirm with you once everything checks out.\n\n— The {orgName} Team' },
    406: { theme: 'orange', subject: 'Ticket #{ticketId} reopened for re-investigation', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been reopened for re-investigation. Our team will keep you updated on their findings.\n\n— The {orgName} Team' },
    407: { theme: 'orange', subject: 'We hear you — ticket #{ticketId} reopened', body: 'Hi {reporterName},\n\nWe have noted that you were not satisfied with the resolution of ticket #{ticketId}. The ticket has been reopened and we are committed to getting this right for you.\n\n— The {orgName} Team' },
    408: { theme: 'orange', subject: 'Additional work required — ticket #{ticketId}', body: 'Hi {reporterName},\n\nWe have identified that additional work is required for ticket #{ticketId}. The ticket has been reopened and our team is working on it.\n\n— The {orgName} Team' },
    409: { theme: 'orange', subject: 'Quality check — ticket #{ticketId} reopened', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been reopened as the resolution did not meet our quality standards. We will ensure it is fully resolved before closing again.\n\n— The {orgName} Team' },
    410: { theme: 'orange', subject: 'Resolution disputed — ticket #{ticketId}', body: 'Hi {reporterName},\n\nThe proposed resolution for ticket #{ticketId} has been disputed. Our team will review and respond with an updated resolution shortly.\n\n— The {orgName} Team' },
    411: { theme: 'orange', subject: 'Ticket #{ticketId} escalated following closure', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been escalated for further review following its closure. A senior team member will be in touch with you.\n\n— The {orgName} Team' },
    412: { theme: 'orange', subject: 'Regression detected — ticket #{ticketId} reopened', body: 'Hi {reporterName},\n\nA regression related to ticket #{ticketId} has been detected. The ticket has been reopened for immediate attention from our team.\n\n— The {orgName} Team' },
    501: { theme: 'red',    subject: 'Ticket #{ticketId} escalated to senior team', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been escalated to our senior team. You will now receive priority attention.\n\n— The {orgName} Team' },
    502: { theme: 'red',    subject: 'Priority raised — ticket #{ticketId}', body: 'Hi {reporterName},\n\nThe priority of your ticket #{ticketId} has been raised. Our team will address it with urgency.\n\n— The {orgName} Team' },
    503: { theme: 'red',    subject: 'Urgent — ticket #{ticketId} receiving immediate attention', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been marked as urgent and is receiving immediate attention from {orgName}.\n\n— The {orgName} Team' },
    504: { theme: 'red',    subject: 'Ticket #{ticketId} flagged for management review', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been flagged for review by our management team. We take your concern very seriously.\n\n— The {orgName} Team' },
    505: { theme: 'red',    subject: 'Critical — ticket #{ticketId}', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been marked as critical. Our team is prioritising this and will update you shortly.\n\n— The {orgName} Team' },
    506: { theme: 'orange', subject: 'Ticket #{ticketId} referred to specialist', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been referred to a specialist for further review. You can expect a detailed response from them shortly.\n\n— The {orgName} Team' },
    507: { theme: 'red',    subject: 'Ticket #{ticketId} — resolution time exceeded', body: 'Hi {reporterName},\n\nWe acknowledge that ticket #{ticketId} has exceeded its expected resolution time. It has been escalated to ensure it is addressed immediately. We apologise for the delay.\n\n— The {orgName} Team' },
    508: { theme: 'red',    subject: 'Formal complaint #{ticketId} escalated to senior leadership', body: 'Hi {reporterName},\n\nYour formal complaint #{ticketId} has been escalated to our senior leadership. We will respond within the committed timeframe.\n\n— The {orgName} Team' },
    509: { theme: 'red',    subject: 'Ticket #{ticketId} — dedicated team assigned', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} has been escalated to ensure you receive our highest level of service. A dedicated team member has been assigned to your case.\n\n— The {orgName} Team' },
    510: { theme: 'red',    subject: 'Ticket #{ticketId} referred for legal review', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been referred for legal review. Our compliance team will be in touch with you directly.\n\n— The {orgName} Team' },
    511: { theme: 'red',    subject: 'Ticket #{ticketId} — potential regulatory matter', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been flagged as a potential regulatory matter and is being reviewed by the appropriate team. We will be in touch shortly.\n\n— The {orgName} Team' },
    512: { theme: 'red',    subject: 'Policy breach identified — ticket #{ticketId}', body: 'Hi {reporterName},\n\nA breach of policy has been identified in relation to ticket #{ticketId}. The matter has been escalated for formal review.\n\n— The {orgName} Team' },
    513: { theme: 'orange', subject: 'Ticket #{ticketId} — senior approval required', body: 'Hi {reporterName},\n\nYour ticket #{ticketId} requires senior-level approval to proceed. Our team has been notified and will update you shortly.\n\n— The {orgName} Team' },
    514: { theme: 'orange', subject: 'Ticket #{ticketId} — coordinated team response', body: 'Hi {reporterName},\n\nTicket #{ticketId} has been shared across multiple teams for a coordinated response. We will keep you updated on progress.\n\n— The {orgName} Team' },
};

const EMAIL_THEME_COLORS = {
    green:  { header: '#1a7a40', accent: '#27ae60' },
    blue:   { header: '#1a5276', accent: '#2980b9' },
    red:    { header: '#922b21', accent: '#c0392b' },
    orange: { header: '#935116', accent: '#d35400' },
    purple: { header: '#6c3483', accent: '#8e44ad' },
};

const STATUS_TEMPLATE_VARIABLES = [
    { key: 'ticketId',       label: '{{ticketId}}',       desc: 'Ticket reference ID' },
    { key: 'ticketStatus',   label: '{{ticketStatus}}',   desc: 'Current status name' },
    { key: 'ticketURL',      label: '{{ticketURL}}',      desc: 'Link to the ticket' },
    { key: 'formTitle',      label: '{{formTitle}}',      desc: 'Form title' },
    { key: 'formURL',        label: '{{formURL}}',        desc: 'Link to the form' },
    { key: 'toUserFullName', label: '{{toUserFullName}}', desc: "Reporter's full name" },
    { key: 'toUserUserName', label: '{{toUserUserName}}', desc: "Reporter's username" },
    { key: 'toUserEmail',    label: '{{toUserEmail}}',    desc: "Reporter's email" },
];

const getStatusChannelType = (variantId) => {
    if (variantId >= 101 && variantId <= 199) return 'status_received';
    if (variantId >= 201 && variantId <= 299) return 'status_inprogress';
    if (variantId >= 301 && variantId <= 399) return 'status_resolved';
    if (variantId >= 401 && variantId <= 499) return 'status_reopened';
    if (variantId >= 501 && variantId <= 599) return 'status_escalated';
    return 'status_received';
};

export default function StatusSettings({ initialStatuses, adminFieldList, onSnackbar, onAfterSave, setCursor }) {
    const fieldService = new FieldService();
    const errorHandler = new ErrorHandler();

    const [editableStatuses, setEditableStatuses] = useState(initialStatuses ?? []);
    const [statusSaving, setStatusSaving] = useState(false);
    const [showStatusTemplates, setShowStatusTemplates] = useState(false);
    const [showPresetPicker, setShowPresetPicker] = useState(false);
    const [presetPickerFilter, setPresetPickerFilter] = useState('all');
    const [presetPickerSearch, setPresetPickerSearch] = useState('');
    const [presetPickerSelected, setPresetPickerSelected] = useState(null);
    const [presetPickerWaReporter, setPresetPickerWaReporter] = useState(true);
    const [presetPickerWaFormAgent, setPresetPickerWaFormAgent] = useState(false);
    const [presetPickerWaAssignee, setPresetPickerWaAssignee] = useState(false);
    const [showStatusViewModal, setShowStatusViewModal] = useState(false);
    const [viewModalIdx, setViewModalIdx] = useState(null);
    const [viewModalTab, setViewModalTab] = useState('status');
    const [viewModalPreset, setViewModalPreset] = useState(null);
    const [viewModalVariantSearch, setViewModalVariantSearch] = useState('');
    const [viewModalWaReporter, setViewModalWaReporter] = useState(true);
    const [viewModalWaFormAgent, setViewModalWaFormAgent] = useState(false);
    const [viewModalWaAssignee, setViewModalWaAssignee] = useState(false);
    const [viewModalEmailEnabled, setViewModalEmailEnabled] = useState(false);
    const [viewModalEmailTheme, setViewModalEmailTheme] = useState('blue');
    const [viewModalEmailSubject, setViewModalEmailSubject] = useState('');
    const [viewModalEmailBody, setViewModalEmailBody] = useState('');
    const svmSubjectRef = useRef(null);
    const emailDesignerRef = useRef(null);
    const [svmLeftTab, setSvmLeftTab] = useState('blocks');
    const [svmWaPreviewRecipient, setSvmWaPreviewRecipient] = useState('reporter');
    const [statusColorPickerAnchor, setStatusColorPickerAnchor] = useState(null);
    const [statusColorPickerIdx, setStatusColorPickerIdx] = useState(null);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const viewModalBaselineRef = useRef(null);
    const emailDesignerSeedingRef = useRef(false);
    const emailDesignerWasMountedRef = useRef(false);
    const pendingStatusesRef = useRef(null);
    const statusSavingRef = useRef(false);

    useEffect(() => {
        setEditableStatuses(initialStatuses ?? []);
    }, [initialStatuses]);

    // Every change on this page saves straight away — there is no separate page-level save.
    // Changes made while a save is in flight are queued and the latest list wins, so quick
    // successive actions (e.g. several reorder clicks) are never dropped.
    const persistStatuses = async (nextStatuses, successMessage = 'Status settings saved successfully!') => {
        const reindexed = nextStatuses.map((s, i) => ({ ...s, id: i }));
        setEditableStatuses(reindexed);
        pendingStatusesRef.current = reindexed;
        if (statusSavingRef.current) return;
        statusSavingRef.current = true;
        setStatusSaving(true);
        setCursor('wait');
        try {
            const statusField = adminFieldList?.find(f => f.title === 'status');
            if (!statusField) throw new Error('Status field not found');
            while (pendingStatusesRef.current) {
                const toSave = pendingStatusesRef.current;
                pendingStatusesRef.current = null;
                await fieldService.updateField({ ...statusField, options: JSON.stringify(toSave) });
            }
            onSnackbar(successMessage, 'success');
            await onAfterSave();
        } catch (err) {
            pendingStatusesRef.current = null;
            errorHandler.handleError(err);
            onSnackbar('Failed to save status settings', 'error');
        }
        statusSavingRef.current = false;
        setStatusSaving(false);
        setCursor('auto');
    };

    const applyStatusTemplate = (template) => {
        setShowStatusTemplates(false);
        persistStatuses(template.statuses);
    };

    const moveStatus = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= editableStatuses.length) return;
        const arr = [...editableStatuses];
        [arr[idx], arr[target]] = [arr[target], arr[idx]];
        persistStatuses(arr);
    };

    const removeStatus = (idx) => {
        persistStatuses(editableStatuses.filter((_, i) => i !== idx));
    };

    // Title and colour are edited inline, so they are saved once the edit is finished
    // (input blur / colour picker close) rather than on every keystroke.
    const commitStatusTitle = (idx) => {
        const savedTitle = initialStatuses?.[idx]?.title ?? '';
        const title = (editableStatuses[idx]?.title ?? '').trim();
        if (!title) {
            setEditableStatuses(s => s.map((x, i) => i === idx ? { ...x, title: savedTitle } : x));
            return;
        }
        if (title === savedTitle) return;
        persistStatuses(editableStatuses.map((x, i) => i === idx ? { ...x, title } : x));
    };

    const closeStatusColorPicker = () => {
        const idx = statusColorPickerIdx;
        setStatusColorPickerIdx(null);
        setStatusColorPickerAnchor(null);
        if (idx !== null && editableStatuses[idx] && editableStatuses[idx].color !== initialStatuses?.[idx]?.color) {
            persistStatuses(editableStatuses);
        }
    };

    const openPresetPicker = () => {
        setPresetPickerFilter('all');
        setPresetPickerSearch('');
        setPresetPickerSelected(null);
        setPresetPickerWaReporter(true);
        setPresetPickerWaFormAgent(false);
        setPresetPickerWaAssignee(false);
        setShowPresetPicker(true);
    };

    const closePresetPicker = () => {
        setShowPresetPicker(false);
        setPresetPickerSelected(null);
        setPresetPickerSearch('');
    };

    const addPresetStatus = (preset) => {
        const emailDefaults = EMAIL_VARIANT_DEFAULTS[preset.whatsappVariantId] ?? {};
        setShowPresetPicker(false);
        persistStatuses([...editableStatuses, {
            value: preset.value,
            title: preset.title,
            color: preset.defaultColor,
            whatsappVariantId: preset.whatsappVariantId,
            whatsappEnabled: presetPickerWaReporter || presetPickerWaFormAgent || presetPickerWaAssignee,
            whatsappReporter: presetPickerWaReporter,
            whatsappFormAgent: presetPickerWaFormAgent,
            whatsappAssignee: presetPickerWaAssignee,
            emailEnabled: false,
            emailTheme: emailDefaults.theme ?? 'blue',
            emailSubject: emailDefaults.subject ?? '',
            emailBody: emailDefaults.body ?? '',
        }]);
    };

    const openStatusViewModal = (idx) => {
        const status = editableStatuses[idx];
        const emailDefaults = EMAIL_VARIANT_DEFAULTS[status.whatsappVariantId] ?? {};
        setViewModalIdx(idx);
        setViewModalTab('status');
        setViewModalPreset(STATUS_PRESET_REGISTRY.find(p => p.whatsappVariantId === status.whatsappVariantId) ?? null);
        setViewModalVariantSearch('');
        setViewModalWaReporter(status.whatsappReporter ?? status.whatsappEnabled ?? true);
        setViewModalWaFormAgent(status.whatsappFormAgent ?? false);
        setViewModalWaAssignee(status.whatsappAssignee ?? false);
        setViewModalEmailEnabled(status.emailEnabled ?? false);
        setViewModalEmailTheme(status.emailTheme ?? emailDefaults.theme ?? 'blue');
        setViewModalEmailSubject(status.emailSubject ?? emailDefaults.subject ?? '');
        setViewModalEmailBody(status.emailBody ?? emailDefaults.body ?? '');
        // What the modal opened with — compared against on close to detect unsaved edits.
        viewModalBaselineRef.current = {
            whatsappVariantId: status.whatsappVariantId,
            waReporter: status.whatsappReporter ?? status.whatsappEnabled ?? true,
            waFormAgent: status.whatsappFormAgent ?? false,
            waAssignee: status.whatsappAssignee ?? false,
            emailEnabled: status.emailEnabled ?? false,
            emailTheme: status.emailTheme ?? emailDefaults.theme ?? 'blue',
            emailSubject: status.emailSubject ?? emailDefaults.subject ?? '',
            emailBody: status.emailBody ?? emailDefaults.body ?? '',
        };
        setSvmLeftTab('blocks');
        setSvmWaPreviewRecipient('reporter');
        setShowStatusViewModal(true);
    };

    const closeStatusViewModal = () => {
        setShowStatusViewModal(false);
        setShowDiscardConfirm(false);
        setViewModalIdx(null);
    };

    const isStatusViewModalDirty = () => {
        const base = viewModalBaselineRef.current;
        if (!base) return false;
        return (viewModalPreset != null && viewModalPreset.whatsappVariantId !== base.whatsappVariantId)
            || viewModalWaReporter !== base.waReporter
            || viewModalWaFormAgent !== base.waFormAgent
            || viewModalWaAssignee !== base.waAssignee
            || viewModalEmailEnabled !== base.emailEnabled
            || viewModalEmailTheme !== base.emailTheme
            || viewModalEmailSubject !== base.emailSubject
            || viewModalEmailBody !== base.emailBody;
    };

    // Close via the X, Cancel or a click outside: ask first if there are unsaved edits.
    const requestCloseStatusViewModal = () => {
        if (isStatusViewModalDirty()) {
            setShowDiscardConfirm(true);
        } else {
            closeStatusViewModal();
        }
    };

    // The email designer re-exports its content as soon as it mounts, which can differ from the
    // stored body without the user touching anything. That first export is adopted as the
    // baseline (when the body was still unedited) so it isn't mistaken for an unsaved change.
    const handleEmailBodyChange = (html) => {
        if (emailDesignerSeedingRef.current) {
            emailDesignerSeedingRef.current = false;
            const base = viewModalBaselineRef.current;
            if (base && viewModalEmailBody === base.emailBody) base.emailBody = html;
        }
        setViewModalEmailBody(html);
    };

    // Switching variant re-seeds the email draft with the new variant's defaults, unless this
    // status already has its own saved email content.
    const selectViewModalPreset = (preset) => {
        setViewModalPreset(preset);
        const status = editableStatuses[viewModalIdx];
        const emailDefaults = EMAIL_VARIANT_DEFAULTS[preset.whatsappVariantId] ?? {};
        if (status?.emailTheme == null) setViewModalEmailTheme(emailDefaults.theme ?? 'blue');
        if (status?.emailSubject == null) setViewModalEmailSubject(emailDefaults.subject ?? '');
        if (status?.emailBody == null) setViewModalEmailBody(emailDefaults.body ?? '');
    };

    // Title and colour only follow the variant when a different one is picked, so a status that
    // was renamed or recoloured inline keeps its own label until its variant actually changes.
    const getViewModalVariantPatch = (status) => (
        viewModalPreset && viewModalPreset.whatsappVariantId !== status.whatsappVariantId
            ? { title: viewModalPreset.title, color: viewModalPreset.defaultColor, whatsappVariantId: viewModalPreset.whatsappVariantId }
            : {}
    );

    const saveStatusViewModal = () => {
        const updatedStatuses = editableStatuses.map((x, i) => i === viewModalIdx ? {
            ...x,
            ...getViewModalVariantPatch(x),
            whatsappReporter: viewModalWaReporter,
            whatsappFormAgent: viewModalWaFormAgent,
            whatsappAssignee: viewModalWaAssignee,
            whatsappEnabled: viewModalWaReporter || viewModalWaFormAgent || viewModalWaAssignee,
            emailEnabled: viewModalEmailEnabled,
            emailTheme: viewModalEmailTheme,
            emailSubject: viewModalEmailSubject,
            emailBody: viewModalEmailBody,
        } : x);
        closeStatusViewModal();
        persistStatuses(updatedStatuses);
    };

    const emailDesignerMounted = showStatusViewModal && viewModalTab === 'email' && viewModalEmailEnabled;
    if (emailDesignerMounted && !emailDesignerWasMountedRef.current) emailDesignerSeedingRef.current = true;
    emailDesignerWasMountedRef.current = emailDesignerMounted;

    return (
        <>
            {/* ── Main status panel ── */}
            <div className={`${cn}__status`}>

                {/* ── Header ── */}
                <div className={`${cn}__status__header`}>
                    <div className={`${cn}__status__header__inner`}>
                        <div>
                            <h2 className={`${cn}__status__title`}>Status Settings</h2>
                            <p className={`${cn}__status__desc`}>
                                Customise the status workflow for this form. Rename labels, change colours, add custom stages, or start from a template.
                            </p>
                        </div>
                        <div className={`${cn}__status__header__actions`}>
                            {statusSaving && <span className={`${cn}__status__desc`}>Saving…</span>}
                            <button
                                className={`${cn}__status__btn --ghost`}
                                onClick={() => setShowStatusTemplates(true)}
                            >
                                Templates
                            </button>
                        </div>
                    </div>
                </div>

                <div className={`${cn}__status__content`}>

                    {/* ── System status notice ── */}
                    <div className={`${cn}__status__notice`}>
                        <Info size={14} style={{ flexShrink: 0 }} />
                        <span>
                            Statuses labelled <strong>System</strong> power built-in actions (Escalate, Mark Resolved, Re-Open) and cannot be deleted.
                            You can freely rename them and change their colour.
                        </span>
                    </div>

                    {/* ── Editable status list ── */}
                    <div className={`${cn}__status__editor`}>
                        {editableStatuses.map((status, idx) => (
                            <div key={idx} className={`${cn}__status__row`}>

                                {/* Reorder buttons */}
                                <div className={`${cn}__status__row__reorder`}>
                                    <button
                                        className={`${cn}__status__row__reorderBtn`}
                                        disabled={idx === 0}
                                        onClick={() => moveStatus(idx, -1)}
                                        title="Move up"
                                    >
                                        <ShiftUp />
                                    </button>
                                    <button
                                        className={`${cn}__status__row__reorderBtn`}
                                        disabled={idx === editableStatuses.length - 1}
                                        onClick={() => moveStatus(idx, 1)}
                                        title="Move down"
                                    >
                                        <ShiftDown />
                                    </button>
                                </div>

                                {/* Colour swatch — opens picker */}
                                <button
                                    className={`${cn}__status__row__swatch`}
                                    style={{ background: status.color }}
                                    title="Change colour"
                                    onClick={(e) => {
                                        if (statusColorPickerIdx === idx) {
                                            closeStatusColorPicker();
                                        } else {
                                            setStatusColorPickerIdx(idx);
                                            setStatusColorPickerAnchor(e.currentTarget);
                                        }
                                    }}
                                />

                                {/* Title input */}
                                <input
                                    className={`${cn}__status__row__input`}
                                    value={status.title}
                                    maxLength={60}
                                    onChange={e => setEditableStatuses(s =>
                                        s.map((x, i) => i === idx
                                            ? { ...x, title: DOMPurify.sanitize(e.target.value) }
                                            : x
                                        )
                                    )}
                                    onBlur={() => commitStatusTitle(idx)}
                                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                />

                                {/* Value tag (read-only hint) */}
                                <span className={`${cn}__status__row__valueTag`}>
                                    {status.value}
                                </span>

                                {/* Edit — variant plus WhatsApp & Email notifications */}
                                <button
                                    className={`${cn}__status__row__changeBtn`}
                                    title="Edit status variant & notifications"
                                    onClick={() => openStatusViewModal(idx)}
                                >
                                    <Edit size={14} />
                                </button>

                                {/* Delete — only for non-system statuses */}
                                {!SYSTEM_STATUS_VALUES.has(status.value) && (
                                    <button
                                        className={`${cn}__status__row__deleteBtn`}
                                        title="Remove status"
                                        onClick={() => removeStatus(idx)}
                                    >
                                        <Delete size={16} />
                                    </button>
                                )}
                            </div>
                        ))}

                        {/* Add custom status */}
                        <button
                            className={`${cn}__status__addBtn`}
                            onClick={openPresetPicker}
                        >
                            + Add Status
                        </button>
                    </div>
                </div>{/* end __status__content */}

                {/* Shared colour picker */}
                {statusColorPickerIdx !== null && (
                    <ColorPicker
                        showColorMenu={true}
                        anchorColorMenu={statusColorPickerAnchor}
                        color={editableStatuses[statusColorPickerIdx]?.color || '#2784B9'}
                        handleColorChange={(color) => {
                            setEditableStatuses(s =>
                                s.map((x, i) => i === statusColorPickerIdx
                                    ? { ...x, color: color.hex }
                                    : x
                                )
                            );
                        }}
                        handleColorPickerMenu={closeStatusColorPicker}
                    />
                )}
            </div>

            {/* ── Status Template Modal ── */}
            <Dialog
                open={showStatusTemplates}
                onClose={() => setShowStatusTemplates(false)}
                maxWidth="md"
                fullWidth
                className={`${cn}__tplModal`}
                PaperProps={{ className: `${cn}__tplModal__paper` }}
            >
                <DialogTitle className={`${cn}__tplModal__title`}>
                    <span>Choose a Status Template</span>
                    <button
                        className={`${cn}__tplModal__close`}
                        onClick={() => setShowStatusTemplates(false)}
                        aria-label="Close"
                    >
                        <BsClose />
                    </button>
                </DialogTitle>
                <DialogContent className={`${cn}__tplModal__content`}>
                    <p className={`${cn}__tplModal__hint`}>
                        Selecting a template replaces all current statuses. You can still rename and recolour after applying.
                    </p>
                    <div className={`${cn}__tplModal__grid`}>
                        {STATUS_TEMPLATES.map(tpl => (
                            <button
                                key={tpl.id}
                                className={`${cn}__tplModal__card`}
                                onClick={() => applyStatusTemplate(tpl)}
                            >
                                <div className={`${cn}__tplModal__card__header`}>
                                    <strong className={`${cn}__tplModal__card__name`}>{tpl.name}</strong>
                                    <span className={`${cn}__tplModal__card__count`}>{tpl.statuses.length} statuses</span>
                                </div>
                                <span className={`${cn}__tplModal__card__desc`}>{tpl.description}</span>
                                <div className={`${cn}__tplModal__card__flow`}>
                                    {tpl.statuses.map((s, si) => (
                                        <div key={si} className={`${cn}__tplModal__card__flowItem`}>
                                            <div
                                                className={`${cn}__tplModal__card__pill`}
                                                style={{ borderColor: s.color }}
                                            >
                                                <span
                                                    className={`${cn}__tplModal__card__pill__dot`}
                                                    style={{ background: s.color }}
                                                />
                                                <span
                                                    className={`${cn}__tplModal__card__pill__label`}
                                                    style={{ color: s.color }}
                                                >
                                                    {s.title}
                                                </span>
                                            </div>
                                            {si < tpl.statuses.length - 1 && (
                                                <span className={`${cn}__tplModal__card__arrow`}>›</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Preset Picker ── */}
            {showPresetPicker && (
                <div className={`${cn}__modal__overlay`} onClick={closePresetPicker}>
                    <div className={`${cn}__presetPicker`} onClick={e => e.stopPropagation()}>

                        {/* ── Header ── */}
                        <div className={`${cn}__presetPicker__hdr`}>
                            <h3>Add Status</h3>
                            <button className={`${cn}__presetPicker__hdr__close`} onClick={closePresetPicker} title="Close">
                                <XIcon size={18} />
                            </button>
                        </div>

                        {/* ── Two-pane body ── */}
                        <div className={`${cn}__presetPicker__body`}>

                            {/* ── Left: search + filter + list ── */}
                            <div className={`${cn}__presetPicker__left`}>
                                <div className={`${cn}__presetPicker__left__search`}>
                                    <SearchIcon size={14} />
                                    <input
                                        type="text"
                                        placeholder="Search variants…"
                                        value={presetPickerSearch}
                                        onChange={e => setPresetPickerSearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className={`${cn}__presetPicker__left__filters`}>
                                    {['all', 'reported', 'open', 'resolved', 'reopened', 'escalated'].map(v => (
                                        <button
                                            key={v}
                                            className={presetPickerFilter === v ? '--active' : ''}
                                            onClick={() => setPresetPickerFilter(v)}
                                        >
                                            {v === 'all' ? 'All' : v.charAt(0).toUpperCase() + v.slice(1)}
                                        </button>
                                    ))}
                                </div>
                                <div className={`${cn}__presetPicker__left__list`}>
                                    {STATUS_PRESET_REGISTRY
                                        .filter(p =>
                                            (presetPickerFilter === 'all' || p.value === presetPickerFilter) &&
                                            !editableStatuses.some(s => s.title === p.title) &&
                                            (presetPickerSearch === '' || p.title.toLowerCase().includes(presetPickerSearch.toLowerCase()))
                                        )
                                        .map(p => (
                                            <button
                                                key={p.presetId}
                                                className={`${cn}__presetPicker__left__item${presetPickerSelected?.presetId === p.presetId ? ' --active' : ''}`}
                                                onClick={() => setPresetPickerSelected(p)}
                                            >
                                                <span
                                                    className={`${cn}__presetPicker__left__item__swatch`}
                                                    style={{ background: p.defaultColor }}
                                                />
                                                <span className={`${cn}__presetPicker__left__item__title`}>{p.title}</span>
                                                <span className={`${cn}__presetPicker__left__item__badge --${p.value}`}>
                                                    {p.value}
                                                </span>
                                            </button>
                                        ))
                                    }
                                </div>
                            </div>

                            {/* ── Right: configuration panel ── */}
                            <div className={`${cn}__presetPicker__right`}>
                                {!presetPickerSelected ? (
                                    <div className={`${cn}__presetPicker__right__empty`}>
                                        <MessageIcon size={32} />
                                        <p>Select a variant from the list to configure it</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Variant heading */}
                                        <div className={`${cn}__presetPicker__right__detailHdr`}>
                                            <span
                                                className={`${cn}__presetPicker__right__detailHdr__swatch`}
                                                style={{ background: presetPickerSelected.defaultColor }}
                                            />
                                            <span className={`${cn}__presetPicker__right__detailHdr__name`}>
                                                {presetPickerSelected.title}
                                            </span>
                                            <span className={`${cn}__presetPicker__right__detailHdr__badge --${presetPickerSelected.value}`}>
                                                {presetPickerSelected.value}
                                            </span>
                                        </div>

                                        {/* ── WhatsApp notification toggles ── */}
                                        <div className={`${cn}__presetPicker__right__section`}>
                                            <p className={`${cn}__presetPicker__right__sectionTitle`}>
                                                <WhatsappIcon size={14} />
                                                WhatsApp Notifications
                                            </p>
                                            <p className={`${cn}__presetPicker__right__sectionHint`}>
                                                Choose who receives a WhatsApp message when this status is applied
                                            </p>
                                            <div className={`${cn}__presetPicker__right__toggleRow`}>
                                                <div className={`${cn}__presetPicker__right__toggleItem`}>
                                                    <ToggleStyleSwitch
                                                        checked={presetPickerWaReporter}
                                                        onChange={(_, v) => setPresetPickerWaReporter(v)}
                                                    />
                                                    <span>Reporter</span>
                                                </div>
                                                <div className={`${cn}__presetPicker__right__toggleItem`}>
                                                    <ToggleStyleSwitch
                                                        checked={presetPickerWaFormAgent}
                                                        onChange={(_, v) => setPresetPickerWaFormAgent(v)}
                                                    />
                                                    <span>Form Agent</span>
                                                </div>
                                                <div className={`${cn}__presetPicker__right__toggleItem`}>
                                                    <ToggleStyleSwitch
                                                        checked={presetPickerWaAssignee}
                                                        onChange={(_, v) => setPresetPickerWaAssignee(v)}
                                                    />
                                                    <span>Assignee</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ── WhatsApp message preview ── */}
                                        {(presetPickerWaReporter || presetPickerWaFormAgent || presetPickerWaAssignee) && (
                                            <div className={`${cn}__presetPicker__right__section`}>
                                                <p className={`${cn}__presetPicker__right__sectionTitle`}>
                                                    <MessageIcon size={14} />
                                                    Message Preview
                                                </p>
                                                <p className={`${cn}__presetPicker__right__sectionHint`}>
                                                    Sent to: {[
                                                        presetPickerWaReporter && 'Reporter',
                                                        presetPickerWaFormAgent && 'Form Agent',
                                                        presetPickerWaAssignee && 'Assignee',
                                                    ].filter(Boolean).join(', ')}
                                                </p>
                                                <div className={`${cn}__presetPicker__right__waBubble`}>
                                                    <span dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(
                                                            (WHATSAPP_VARIANT_MESSAGES[presetPickerSelected.whatsappVariantId] || '')
                                                                .replace(/{ticketId}/g, 'APCKZQ1023')
                                                                .replace(/{formTitle}/g, 'General Support')
                                                                .replace(/{orgName}/g, 'Your Organisation')
                                                                .replace(/\*(.*?)\*/g, '<b>$1</b>')
                                                        )
                                                    }} />
                                                    <span className={`${cn}__presetPicker__right__waBubble__time`}>12:00</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* ── Email note ── */}
                                        <div className={`${cn}__presetPicker__right__section`}>
                                            <p className={`${cn}__presetPicker__right__sectionTitle`}>
                                                <MailOutline size={14} />
                                                Email Notification
                                            </p>
                                            <p className={`${cn}__presetPicker__right__emailNote`}>
                                                A default email has been prepared for this variant. Use the <strong>Edit</strong> button on the status row to customise the subject, body and colour theme.
                                            </p>
                                        </div>

                                        {/* ── Confirm button ── */}
                                        <div className={`${cn}__presetPicker__right__footer`}>
                                            <button
                                                className={`${cn}__presetPicker__right__footer__confirmBtn`}
                                                onClick={() => addPresetStatus(presetPickerSelected)}
                                            >
                                                + Add Status
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Status Notification Config Modal ── */}
            {showStatusViewModal && viewModalIdx !== null && (() => {
                const savedStatus = editableStatuses[viewModalIdx];
                if (!savedStatus) return null;
                const status = { ...savedStatus, ...getViewModalVariantPatch(savedStatus) };
                const viewModalTabs = ['status', 'whatsapp', 'email'];
                const variantOptions = STATUS_PRESET_REGISTRY.filter(p =>
                    p.value === status.value &&
                    !editableStatuses.some((s, i) => i !== viewModalIdx && s.title === p.title) &&
                    (viewModalVariantSearch === '' || p.title.toLowerCase().includes(viewModalVariantSearch.toLowerCase()))
                );
                const waMsg = WHATSAPP_VARIANT_MESSAGES[status.whatsappVariantId] || '';
                const waCards = [
                    { key: 'reporter',  label: 'Reporter',   hint: 'Ticket submitter',     initial: 'R', value: viewModalWaReporter,  set: setViewModalWaReporter  },
                    { key: 'formAgent', label: 'Form Agent', hint: 'Assigned form agents',  initial: 'A', value: viewModalWaFormAgent, set: setViewModalWaFormAgent },
                    { key: 'assignee',  label: 'Assignee',   hint: 'Ticket assignee',       initial: 'T', value: viewModalWaAssignee,  set: setViewModalWaAssignee  },
                ];

                const getWaPreviewForRecipient = (recipientKey) => {
                    const boldify = (text) => text.replace(/\*(.*?)\*/g, '<b>$1</b>').replace(/\n/g, '<br/>');
                    if (recipientKey === 'reporter') {
                        const substituted = waMsg
                            .replace(/{ticketId}/g, 'APCKZQ1023')
                            .replace(/{formTitle}/g, 'General Support')
                            .replace(/{orgName}/g, 'Your Organisation');
                        const body = boldify(
                            `You have received a status update from Your Organisation.\n\n${substituted}\n\nFor your reference:\nForm: General Support\nTicket ID: #APCKZQ1023\nSubmitted: 13 Jun 2026, 10:30 AM\n\nPlease keep this message for your records.`
                        );
                        return {
                            body,
                            buttons: [
                                { type: 'reply', label: 'Download as PDF' },
                                { type: 'url',   label: 'Track Ticket' },
                            ],
                        };
                    }
                    if (recipientKey === 'formAgent') {
                        const body = boldify(
                            `Ticket Status Update\n\nA ticket on *General Support* has had its status updated.\n\nTicket ID: *#APCKZQ1023*\nSubject: *Unable to process payment*\n\nReporter Details\nName: John Smith\nPhone: +919876543210\nEmail: john.smith@example.com\n\nSubmitted on: 13 Jun 2026, 10:30 AM\n\nPlease review and take appropriate action.`
                        );
                        return {
                            body,
                            buttons: [
                                { type: 'reply', label: 'Download as PDF' },
                                { type: 'reply', label: 'View More Details' },
                            ],
                        };
                    }
                    if (recipientKey === 'assignee') {
                        const body = boldify(
                            `Ticket Status Update: *${status.title}*\n\nA ticket assigned to you on *General Support* has had its status updated.\n\nTicket ID: *#APCKZQ1023*\nSubject: *Unable to process payment*\nReporter: John Smith\nSubmitted on: 13 Jun 2026, 10:30 AM\n\nPlease review and take action.`
                        );
                        return {
                            body,
                            buttons: [
                                { type: 'reply', label: 'Download as PDF' },
                                { type: 'reply', label: 'View More Details' },
                            ],
                        };
                    }
                    return null;
                };

                const enabledWaCards = waCards.filter(c => c.value);
                const activePreviewKey = enabledWaCards.find(c => c.key === svmWaPreviewRecipient)
                    ? svmWaPreviewRecipient
                    : enabledWaCards[0]?.key ?? 'reporter';
                const activePreview = getWaPreviewForRecipient(activePreviewKey);
                const isCustomTheme = !EMAIL_THEME_COLORS[viewModalEmailTheme];
                const customColorValue = isCustomTheme ? viewModalEmailTheme : '#2784b9';
                const insertSubjectVar = (varKey) => {
                    const token = `{{${varKey}}}`;
                    const input = svmSubjectRef.current;
                    if (!input) { setViewModalEmailSubject(s => s + token); return; }
                    const start = input.selectionStart ?? viewModalEmailSubject.length;
                    const end   = input.selectionEnd   ?? viewModalEmailSubject.length;
                    const newVal = viewModalEmailSubject.slice(0, start) + token + viewModalEmailSubject.slice(end);
                    setViewModalEmailSubject(newVal);
                    requestAnimationFrame(() => {
                        input.selectionStart = input.selectionEnd = start + token.length;
                        input.focus();
                    });
                };
                return (
                    <div className={`${cn}__svm__overlay`} onClick={requestCloseStatusViewModal}>
                        <div
                            className={`${cn}__svm`}
                            style={{ '--svm-accent': status.color }}
                            onClick={e => e.stopPropagation()}
                        >
                            {/* ── Header ── */}
                            <div className={`${cn}__svm__hdr`}>
                                <div className={`${cn}__svm__hdr__bar`} style={{ background: status.color }} />
                                <div className={`${cn}__svm__hdr__badge`}>
                                    <span className={`${cn}__svm__hdr__badge__dot`} style={{ background: status.color }} />
                                    <span className={`${cn}__svm__hdr__badge__name`}>{status.title}</span>
                                    <span className={`${cn}__svm__hdr__badge__chip`}>{status.value}</span>
                                </div>
                                <button className={`${cn}__svm__hdr__close`} onClick={requestCloseStatusViewModal}>
                                    <XIcon size={16} />
                                </button>
                            </div>

                            {/* ── Segmented tab control ── */}
                            <div className={`${cn}__svm__tabBar`}>
                                <div className={`${cn}__svm__tabTrack`}>
                                    <div
                                        className={`${cn}__svm__tabSlider`}
                                        style={{
                                            width: `calc((100% - 6px) / ${viewModalTabs.length})`,
                                            transform: `translateX(${viewModalTabs.indexOf(viewModalTab) * 100}%)`,
                                        }}
                                    />
                                    <button
                                        className={`${cn}__svm__tabBtn${viewModalTab === 'status' ? ' --active' : ''}`}
                                        onClick={() => setViewModalTab('status')}
                                    >
                                        <Edit size={13} /> Status
                                    </button>
                                    <button
                                        className={`${cn}__svm__tabBtn${viewModalTab === 'whatsapp' ? ' --active' : ''}`}
                                        onClick={() => setViewModalTab('whatsapp')}
                                    >
                                        <WhatsappIcon size={13} /> WhatsApp
                                    </button>
                                    <button
                                        className={`${cn}__svm__tabBtn${viewModalTab === 'email' ? ' --active' : ''}`}
                                        onClick={() => setViewModalTab('email')}
                                    >
                                        <MailOutline size={13} /> Email
                                    </button>
                                </div>
                            </div>

                            {/* ── Body ── */}
                            <div className={`${cn}__svm__body`}>

                                {/* Status tab — pick which variant this status uses */}
                                {viewModalTab === 'status' && (<>
                                    <div className={`${cn}__presetPicker__left`}>
                                        <div className={`${cn}__presetPicker__left__search`}>
                                            <SearchIcon size={14} />
                                            <input
                                                type="text"
                                                placeholder="Search variants…"
                                                value={viewModalVariantSearch}
                                                onChange={e => setViewModalVariantSearch(e.target.value)}
                                            />
                                        </div>
                                        <div className={`${cn}__presetPicker__left__list`}>
                                            {variantOptions.map(p => (
                                                <button
                                                    key={p.presetId}
                                                    className={`${cn}__presetPicker__left__item${viewModalPreset?.presetId === p.presetId ? ' --active' : ''}`}
                                                    onClick={() => selectViewModalPreset(p)}
                                                >
                                                    <span
                                                        className={`${cn}__presetPicker__left__item__swatch`}
                                                        style={{ background: p.defaultColor }}
                                                    />
                                                    <span className={`${cn}__presetPicker__left__item__title`}>{p.title}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={`${cn}__svm__panel`}>
                                        <p className={`${cn}__svm__label`}>Status variant</p>
                                        <p className={`${cn}__presetPicker__right__sectionHint`}>
                                            The variant sets the label, colour and notification wording for this status.
                                            Pick a different one from the list, then use the WhatsApp and Email tabs to choose who is notified.
                                        </p>
                                        {waMsg && (<>
                                            <p className={`${cn}__svm__label`} style={{ marginTop: '1.5rem' }}>Message to reporter</p>
                                            <div className={`${cn}__presetPicker__right__waBubble`}>
                                                <span dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(
                                                        waMsg
                                                            .replace(/{ticketId}/g, 'APCKZQ1023')
                                                            .replace(/{formTitle}/g, 'General Support')
                                                            .replace(/{orgName}/g, 'Your Organisation')
                                                            .replace(/\*(.*?)\*/g, '<b>$1</b>')
                                                    )
                                                }} />
                                                <span className={`${cn}__presetPicker__right__waBubble__time`}>12:00</span>
                                            </div>
                                        </>)}
                                    </div>
                                </>)}

                                {/* WhatsApp tab */}
                                {viewModalTab === 'whatsapp' && (
                                    <div className={`${cn}__svm__panel`}>
                                        <p className={`${cn}__svm__label`}>Notify via WhatsApp</p>
                                        <div className={`${cn}__svm__cardGrid`}>
                                            {waCards.map(({ key, label, hint, initial, value, set }) => (
                                                <div
                                                    key={key}
                                                    className={`${cn}__svm__recipientCard${value ? ' --on' : ''}`}
                                                    style={value ? { borderColor: status.color + '70', boxShadow: `0 0 0 3px ${status.color}18` } : {}}
                                                    onClick={() => set(!value)}
                                                >
                                                    <div
                                                        className={`${cn}__svm__recipientCard__icon`}
                                                        style={value ? { background: status.color + '18', color: status.color } : {}}
                                                    >
                                                        {initial}
                                                    </div>
                                                    <span className={`${cn}__svm__recipientCard__label`}>{label}</span>
                                                    <span className={`${cn}__svm__recipientCard__hint`}>{hint}</span>
                                                    <div
                                                        className={`${cn}__svm__recipientCard__toggle`}
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <ToggleStyleSwitch checked={value} onChange={(_, v) => set(v)} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {enabledWaCards.length > 0 && activePreview && (
                                            <>
                                                <p className={`${cn}__svm__label`} style={{ marginTop: '1.5rem' }}>
                                                    Message Preview
                                                </p>

                                                {/* Recipient selector — only shown when more than one is enabled */}
                                                {enabledWaCards.length > 1 && (
                                                    <div className={`${cn}__svm__previewTabs`}>
                                                        {enabledWaCards.map(c => (
                                                            <button
                                                                key={c.key}
                                                                type="button"
                                                                className={`${cn}__svm__previewTab${activePreviewKey === c.key ? ' --active' : ''}`}
                                                                style={activePreviewKey === c.key ? { color: status.color, borderBottomColor: status.color } : {}}
                                                                onClick={() => setSvmWaPreviewRecipient(c.key)}
                                                            >
                                                                {c.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className={`${cn}__svm__chatWindow`}>
                                                    <div className={`${cn}__svm__chatHeader`}>
                                                        <div className={`${cn}__svm__chatHeader__avatar`}>K</div>
                                                        <div className={`${cn}__svm__chatHeader__info`}>
                                                            <p className={`${cn}__svm__chatHeader__name`}>Kaiziq Support</p>
                                                            <p className={`${cn}__svm__chatHeader__status`}>
                                                                <span className={`${cn}__svm__chatHeader__status__dot`} />
                                                                online
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className={`${cn}__svm__chatScroll`}>
                                                        <div className={`${cn}__svm__chatBubbleWrap`}>
                                                            <div className={`${cn}__svm__chatBubble`}>
                                                                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activePreview.body) }} />
                                                                <span className={`${cn}__svm__chatBubble__meta`}>12:00 ✓✓</span>
                                                            </div>
                                                            {activePreview.buttons.length > 0 && (
                                                                <div className={`${cn}__svm__chatBubble__buttons`}>
                                                                    {activePreview.buttons.map((btn, i) => (
                                                                        <div key={i} className={`${cn}__svm__chatBubble__btn${btn.type === 'url' ? ' --url' : ''}`}>
                                                                            {btn.type === 'url' ? '↗ ' : ''}{btn.label}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Email tab */}
                                {viewModalTab === 'email' && (<>

                                    {/* ── Left sidebar ── */}
                                    {viewModalEmailEnabled && (
                                        <div className={`${cn}__svm__sidebar`}>
                                            <div className={`${cn}__svm__sidebar__tabs`}>
                                                <button
                                                    type="button"
                                                    className={`${cn}__svm__sidebar__tab${svmLeftTab === 'blocks' ? ' --active' : ''}`}
                                                    onClick={() => setSvmLeftTab('blocks')}
                                                >Add Block</button>
                                                <button
                                                    type="button"
                                                    className={`${cn}__svm__sidebar__tab${svmLeftTab === 'templates' ? ' --active' : ''}`}
                                                    onClick={() => setSvmLeftTab('templates')}
                                                >Templates</button>
                                            </div>
                                            <div className={`${cn}__svm__sidebar__list`}>
                                                {svmLeftTab === 'blocks' && EMAIL_BLOCK_TYPES.map(({ type, label, desc }) => (
                                                    <button
                                                        key={type}
                                                        type="button"
                                                        className={`${cn}__svm__sidebar__item`}
                                                        onClick={() => emailDesignerRef.current?.addBlock(type)}
                                                    >
                                                        <span className={`${cn}__svm__sidebar__item__label`}>{label}</span>
                                                        <span className={`${cn}__svm__sidebar__item__desc`}>{desc}</span>
                                                    </button>
                                                ))}
                                                {svmLeftTab === 'templates' && EMAIL_DESIGNER_TEMPLATES
                                                    .filter(t => t.channelType === getStatusChannelType(status.whatsappVariantId))
                                                    .map(tpl => (
                                                        <button
                                                            key={tpl.key}
                                                            type="button"
                                                            className={`${cn}__svm__sidebar__item --template`}
                                                            onClick={() => emailDesignerRef.current?.applyTemplate(tpl.make())}
                                                        >
                                                            <div className={`${cn}__svm__sidebar__item__bar`} style={{ background: tpl.accent }} />
                                                            <span className={`${cn}__svm__sidebar__item__label`}>{tpl.label}</span>
                                                            <span className={`${cn}__svm__sidebar__item__tone`} style={{ color: tpl.accent, background: tpl.accent + '18' }}>{tpl.tone}</span>
                                                            <span className={`${cn}__svm__sidebar__item__desc`}>{tpl.desc}</span>
                                                        </button>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    )}

                                    {/* ── Main email content ── */}
                                    <div className={`${cn}__svm__main`}>
                                        {/* Enable toggle */}
                                        <div
                                            className={`${cn}__svm__emailToggle${viewModalEmailEnabled ? ' --on' : ''}`}
                                            style={viewModalEmailEnabled ? { borderColor: status.color + '60', boxShadow: `0 0 0 3px ${status.color}14` } : {}}
                                            onClick={() => setViewModalEmailEnabled(!viewModalEmailEnabled)}
                                        >
                                            <div className={`${cn}__svm__emailToggle__left`}>
                                                <span className={`${cn}__svm__emailToggle__icon`} style={viewModalEmailEnabled ? { color: status.color } : {}}>
                                                    <MailOutline size={17} />
                                                </span>
                                                <div>
                                                    <p className={`${cn}__svm__emailToggle__title`}>Send email on this status</p>
                                                    <p className={`${cn}__svm__emailToggle__hint`}>Reporter receives a customised email when this status is applied</p>
                                                </div>
                                            </div>
                                            <div onClick={e => e.stopPropagation()}>
                                                <ToggleStyleSwitch checked={viewModalEmailEnabled} onChange={(_, v) => setViewModalEmailEnabled(v)} />
                                            </div>
                                        </div>

                                        {viewModalEmailEnabled && (<>
                                            {/* Colour theme */}
                                            <p className={`${cn}__svm__label`} style={{ marginTop: '1.25rem' }}>Colour Theme</p>
                                            <div className={`${cn}__svm__themeRow`}>
                                                {Object.entries(EMAIL_THEME_COLORS).map(([key, colors]) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        className={`${cn}__svm__themeChip${viewModalEmailTheme === key ? ' --active' : ''}`}
                                                        onClick={() => { setViewModalEmailTheme(key); emailDesignerRef.current?.applyTheme(colors.header, colors.accent); }}
                                                    >
                                                        <span className={`${cn}__svm__themeChip__swatch`} style={{ background: colors.header }} />
                                                        {key.charAt(0).toUpperCase() + key.slice(1)}
                                                    </button>
                                                ))}
                                                <label
                                                    className={`${cn}__svm__themeChip${isCustomTheme ? ' --active' : ''} --custom`}
                                                    title="Pick a custom colour"
                                                >
                                                    <span className={`${cn}__svm__themeChip__swatch`} style={{ background: customColorValue }} />
                                                    Custom
                                                    <input
                                                        type="color"
                                                        className={`${cn}__svm__themeColorInput`}
                                                        value={customColorValue}
                                                        onChange={e => { setViewModalEmailTheme(e.target.value); emailDesignerRef.current?.applyTheme(e.target.value, e.target.value); }}
                                                    />
                                                </label>
                                            </div>

                                            {/* Subject */}
                                            <p className={`${cn}__svm__label`}>Subject</p>
                                            <input
                                                ref={svmSubjectRef}
                                                className={`${cn}__svm__input`}
                                                value={viewModalEmailSubject}
                                                onChange={e => setViewModalEmailSubject(e.target.value)}
                                                placeholder="e.g. Update on your ticket {{ticketId}}"
                                            />
                                            <div className={`${cn}__svm__chips`}>
                                                <span className={`${cn}__svm__chips__hint`}>Insert:</span>
                                                {STATUS_TEMPLATE_VARIABLES.map(v => (
                                                    <button
                                                        key={v.key}
                                                        type="button"
                                                        className={`${cn}__svm__chips__tag`}
                                                        title={v.desc}
                                                        onClick={() => insertSubjectVar(v.key)}
                                                    >
                                                        {v.label}
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Email body block designer */}
                                            <div className={`${cn}__svm__emailDesigner`}>
                                                <EmailDesigner
                                                    key={`svm-${viewModalIdx}`}
                                                    ref={emailDesignerRef}
                                                    value={viewModalEmailBody}
                                                    onChange={handleEmailBodyChange}
                                                    variables={STATUS_TEMPLATE_VARIABLES}
                                                    channelType={getStatusChannelType(status.whatsappVariantId)}
                                                    sidebarMode={true}
                                                />
                                            </div>
                                        </>)}
                                    </div>
                                </>)}
                            </div>

                            {/* ── Footer ── */}
                            <div className={`${cn}__svm__footer`}>
                                <button className={`${cn}__svm__footer__cancel`} onClick={requestCloseStatusViewModal}>Cancel</button>
                                <button
                                    className={`${cn}__svm__footer__save`}
                                    style={{ background: status.color, boxShadow: `0 4px 14px ${status.color}55` }}
                                    onClick={saveStatusViewModal}
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Unsaved changes confirmation ── */}
            <Dialog
                open={showDiscardConfirm}
                onClose={() => setShowDiscardConfirm(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Discard unsaved changes?</DialogTitle>
                <DialogContent>
                    You have changes to this status that haven't been saved. Closing now will discard them.
                </DialogContent>
                <DialogActions className={`${cn}__svm__footer`}>
                    <button className={`${cn}__svm__footer__cancel`} onClick={() => setShowDiscardConfirm(false)}>
                        Keep Editing
                    </button>
                    <button className={`${cn}__svm__footer__cancel`} onClick={closeStatusViewModal}>
                        Discard
                    </button>
                </DialogActions>
            </Dialog>
        </>
    );
}
